import { useField } from 'formik';

import { DROPDOWN_WIDTHS } from '@hookdeck/theme';

import { User } from '../../../../../../typings/User.interface';
import { capitalizeFirstLetter } from '../../../utils';
import { formatDate, getBrowserTimezone, relative_dates, RelativeDate } from '../../../utils/date';
import { getErrorCodeLabels } from '../../../utils/error-codes';
import isEmpty from '../../../utils/isEmpty';
import { rejection_cause_labels } from '../../../utils/rejection-causes';
import { getOutpostTenantLabel } from '../../scenes/OutpostScene/utils/tenantDisplay';
import { IconName } from '../base/Icon';
import Text from '../base/Text';
import { Div } from '../helpers/StyledUtils';
import Search from '../Search';
import CliUserFilter from './FilterComponents/CliUserFilter';
import DateFilter from './FilterComponents/DateFilter';
import DateFilterVisible from './FilterComponents/DateFilterVisible';
import {
  JSONFilterBody,
  JSONFilterHeaders,
  JSONFilterPath,
  JSONFilterQuery,
} from './FilterComponents/JSONFilter';
import MultiSelectFilter from './FilterComponents/MultiSelectFilter';
import NumberRangeFilter from './FilterComponents/NumberRangeFilter';
import OutpostTenantsFilter from './FilterComponents/Outpost/OutpostTenantsFilter';
import OutpostTopicsFilter from './FilterComponents/Outpost/OutpostTopicsFilter';
import ResourcesFilter from './FilterComponents/ResourcesFilter';
import SelectFilter from './FilterComponents/SelectFilter';
import TextFilter from './FilterComponents/TextFilter';

export type FilterFormValues = {
  date?: {
    relative?: RelativeDate;
    min?: string;
    max?: string;
  };
  webhook_id?: string[];
  request_body?: string;
  request_headers?: string;
  request_query?: string;
  request_path?: string;
  source_id?: string[];
  destination_id?: string[];
  delivery_group?: (string | null)[];
  response_status?: {
    min?: number;
    max?: number;
  };
  error_code?: string[];
  status?: string | string[];
  attempts?: {
    min?: number;
    max?: number;
  };
  cli_user_id?: string[];
  tenant_id?: string[];
  topic?: string[];
  event_id?: string;
  outpost_destination_id?: string;
};

export type FilterComponent = {
  label: string;
  hide_label?: boolean;
  filter_key: string;
  form_name: string;
  Component: React.FC<any> | null;
  component_props?: any;
  icon: IconName;
  center_label?: boolean;
  width?: number;
  hidden?: boolean;
  priority: 'primary' | 'secondary';
  validate?: (values: { [k: string]: any } | string[] | string) => { [k: string]: any };
  isActive: (values: { [k: string]: any } | string[] | string | string[] | string) => boolean;
  formatForForm: (
    filters: { [k: string]: any } | string[] | string | null,
    all_filters: { [k: string]: any },
  ) => { [k: string]: any } | (string | null)[] | string;
  formatForQuery: (
    values: { [k: string]: any } | (string | null)[] | string,
  ) => { [k: string]: any } | (string | null)[] | string | null | undefined;
  formatForDB: (
    values: { [k: string]: any } | string[] | string,
  ) => { [k: string]: any } | undefined;
  formatForMemory?: (
    values: { [k: string]: any } | string[] | string,
  ) => { [k: string]: any } | undefined;
  valuesToString: (
    values: { [k: string]: any } | string[] | string,
    data_by_id: {
      webhook_id: Record<string, Partial<Record<'full_name' | 'name', string>>>;
      destination_id: Record<string, Partial<Record<'name', string>>>;
      source_id: Record<string, Partial<Record<'name', string>>>;
      team_members: Record<string, Partial<Record<'user_id' | 'user_name', string>>>;
      tenant_id?: Record<string, { metadata?: Record<string, unknown> }>;
    },
    user?: User,
    timezone?: string,
  ) => string | undefined;
};

const DEFAULT_MAX_LENGTH = 30;
const REQUEST_MAX_LENGTH = 60;

const truncateFilterValue = (
  items: Array<string | undefined>,
  maxLength = DEFAULT_MAX_LENGTH,
): string => {
  const filtered_items = items.filter((item): item is string => !!item);

  if (filtered_items.length === 0) return '';

  let result = '';
  let displayedCount = 0;

  for (let i = 0; i < filtered_items.length; i++) {
    const item = filtered_items[i];
    const separator = i === 0 ? '' : ', ';
    const testString = result + separator + item;

    if (testString.length <= maxLength) {
      result = testString;
      displayedCount++;
    } else {
      // If we haven't displayed any items yet, show at least the first one truncated
      if (displayedCount === 0) {
        const availableLength = maxLength - 3; // Reserve space for "..."
        result = item.substring(0, availableLength) + '...';
        displayedCount++;
      }
      break;
    }
  }

  const remaining = filtered_items.length - displayedCount;
  if (remaining > 0) {
    result += `... +${remaining} more`;
  }

  return result;
};

const numberRangeFormatForQuery = (v?: { min?: number; max?: number }) => {
  if (!v) {
    return v;
  }
  return {
    ...(!isEmpty(v.min) ? { min: v.min } : {}),
    ...(!isEmpty(v.max) ? { max: v.max } : {}),
  };
};

const numberRangeFormatForDB = (v: { min?: number; max?: number }) => {
  return {
    ...(!isEmpty(v.min) ? { min: v.min } : {}),
    ...(!isEmpty(v.max) ? { max: v.max } : {}),
  };
};

const common_formatters: Record<
  string,
  (
    key: string,
  ) => Pick<
    FilterComponent,
    | 'center_label'
    | 'isActive'
    | 'formatForForm'
    | 'formatForQuery'
    | 'formatForDB'
    | 'valuesToString'
  >
> = {
  multi_select: (key) => ({
    center_label: true,
    isActive: (v: string[]) => {
      if (v?.length > 0) {
        return true;
      }
      return false;
    },
    formatForForm: (filters) => filters || [],
    formatForQuery: (v: string[]) => (v?.length > 0 ? v : undefined),
    formatForDB: (v: string[]) => (v?.length > 0 ? { [key]: v } : undefined),
    valuesToString: (v: string[]) => truncateFilterValue(v.map(capitalizeFirstLetter)),
  }),
  select: (key) => ({
    center_label: true,
    isActive: (v: string[]) => {
      if (v?.length > 0) {
        return true;
      }
      return false;
    },
    formatForForm: (filters) => filters || '',
    formatForQuery: (v: string) => (v?.length > 0 ? v : undefined),
    formatForDB: (v: string) => (v?.length > 0 ? { [key]: v } : undefined),
    valuesToString: capitalizeFirstLetter,
  }),
  single_value: (key) => ({
    center_label: true,
    isActive: (v: string[]) => {
      if (v?.length > 0) {
        return true;
      }
      return false;
    },
    formatForForm: (v) => v || '',
    formatForQuery: (v) => v || undefined,
    formatForDB: (v) => ({ [key]: v }),
    valuesToString: (v: string) => v,
  }),
};

const hidden_date_filter = {
  label: 'Date',
  filter_key: 'date',
  form_name: 'date',
  Component: DateFilter,
  icon: 'date',
  center_label: true,
  hidden: true,
  priority: 'primary',
  isActive: (v: { relative: string; min: string; max: string }) => {
    if (v?.relative) {
      return true;
    }
    if (v?.min || v?.max) {
      return true;
    }
    return false;
  },
  formatForForm: (filters) => filters || {},
  formatForMemory: (v: { relative?: string; min?: string; max?: string }) => {
    if (v.relative) {
      delete v.min;
      delete v.max;
    }
    return v;
  },
  formatForQuery: (v: { relative?: string; min?: string; max?: string }) => {
    if (v?.relative) {
      return v.relative;
    }
    delete v?.relative;
    return v;
  },
  formatForDB: (v: { relative: string; min: string; max: string }) => {
    if (v?.relative) {
      return { date: { relative: v.relative } };
    }
    if (v?.min || v?.max) {
      return { date: { min: v.min, max: v.max } };
    }
    return undefined;
  },
  valuesToString: (
    v: { relative: string; min: string; max: string },
    _data_by_id,
    _user,
    timezone = getBrowserTimezone(),
  ) => {
    if (v?.relative) {
      return relative_dates[v.relative].label;
    }
    if (v?.min || v?.max) {
      return `${formatDate(v.min, 'MMM d yy, p', timezone)} -> ${
        v.max ? formatDate(v.max, 'MMM d yy, p', timezone) : 'Now'
      }`;
    }
    return '';
  },
} as const;

const date_filter_visible = {
  ...hidden_date_filter,
  Component: DateFilterVisible,
  hidden: false,
  width: 670,
};

const event_status_options = [
  { value: 'successful', label: 'Successful' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Pending' },
  { value: 'paused', label: 'Paused' },
  { value: 'scheduled', label: 'Delayed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const filters_components: Record<string, FilterComponent> = {
  date: hidden_date_filter,
  date_visible: date_filter_visible,
  request_body: {
    label: 'Request Body',
    priority: 'primary',
    hide_label: true,
    filter_key: 'request.body',
    form_name: 'request_body',
    Component: JSONFilterBody,
    width: DROPDOWN_WIDTHS.xlarge,
    icon: 'data_object',
    validate: (body) => {
      const errors: { body?: string } = {};
      try {
        !!body && JSON.parse(body as string);
      } catch (e) {
        errors.body = 'Invalid JSON';
      }

      return errors;
    },
    isActive: (body: string) => {
      return !!body && body !== '{}';
    },
    formatForForm: (_, all_filters) => {
      return all_filters?.request?.body || '{}';
    },
    formatForMemory: (v: string) => {
      if (v && v !== '{}') {
        return { body: v };
      }
      return undefined;
    },
    formatForQuery: (v: string) => {
      if (v && v !== '{}') {
        return { body: v };
      }
      return undefined;
    },
    formatForDB: (v: string) => {
      if (v && v !== '{}') {
        return { request: { body: v } };
      }
      return undefined;
    },
    valuesToString: (v: string) => {
      if (!v || v === '{}') return undefined;
      return v.length > REQUEST_MAX_LENGTH ? v.substring(0, REQUEST_MAX_LENGTH) + '...' : v;
    },
  },
  request_headers: {
    label: 'Request Headers',
    priority: 'secondary',
    hide_label: true,
    filter_key: 'request.headers',
    form_name: 'request_headers',
    Component: JSONFilterHeaders,
    width: DROPDOWN_WIDTHS.xlarge,
    icon: 'data_object',
    validate: (headers) => {
      const errors: { headers?: string } = {};
      try {
        if (headers) {
          const parsed_headers = JSON.parse(headers as string);
          if (typeof parsed_headers !== 'object' || Array.isArray(parsed_headers)) {
            errors.headers = 'Headers must be an object';
          } else if (Object.values(parsed_headers).some((value) => typeof value !== 'string')) {
            errors.headers = 'Header values must be strings';
          }
        }
      } catch (e) {
        errors.headers = 'Invalid JSON';
      }
      return errors;
    },
    isActive: (headers: string) => {
      return !!headers && headers !== '{}';
    },
    formatForForm: (_, all_filters) => {
      return all_filters?.request?.headers || '{}';
    },
    formatForMemory: (v: string) => {
      if (v && v !== '{}') {
        return { headers: v };
      }
      return undefined;
    },
    formatForQuery: (v: string) => {
      if (v && v !== '{}') {
        return { headers: v };
      }
      return undefined;
    },
    formatForDB: (v: string) => {
      if (v && v !== '{}') {
        return { request: { headers: v } };
      }
      return undefined;
    },
    valuesToString: (v: string) => {
      if (!v || v === '{}') return undefined;
      return v.length > REQUEST_MAX_LENGTH ? v.substring(0, REQUEST_MAX_LENGTH) + '...' : v;
    },
  },
  request_query: {
    label: 'Request Query',
    priority: 'secondary',
    hide_label: true,
    filter_key: 'request.query',
    form_name: 'request_query',
    Component: JSONFilterQuery,
    width: DROPDOWN_WIDTHS.xlarge,
    icon: 'data_object',
    validate: (parsed_query) => {
      const errors: { parsed_query?: string } = {};
      try {
        if (parsed_query) {
          const parsed = JSON.parse(parsed_query as string);
          if (typeof parsed !== 'object' || Array.isArray(parsed)) {
            errors.parsed_query = 'Query must be an object';
          } else if (Object.values(parsed).some((value) => typeof value !== 'string')) {
            errors.parsed_query = 'Query values must be strings';
          }
        }
      } catch (e) {
        errors.parsed_query = 'Invalid JSON';
      }
      return errors;
    },
    isActive: (parsed_query: string) => {
      return !!parsed_query && parsed_query !== '{}';
    },
    formatForForm: (_, all_filters) => {
      return all_filters?.request?.parsed_query || '{}';
    },
    formatForMemory: (v: string) => {
      if (v && v !== '{}') {
        return { parsed_query: v };
      }
      return undefined;
    },
    formatForQuery: (v: string) => {
      if (v && v !== '{}') {
        return { parsed_query: v };
      }
      return undefined;
    },
    formatForDB: (v: string) => {
      if (v && v !== '{}') {
        return { request: { parsed_query: v } };
      }
      return undefined;
    },
    valuesToString: (v: string) => {
      if (!v || v === '{}') return undefined;
      return v.length > REQUEST_MAX_LENGTH ? v.substring(0, REQUEST_MAX_LENGTH) + '...' : v;
    },
  },
  request_path: {
    label: 'Request Path',
    priority: 'secondary',
    hide_label: true,
    filter_key: 'request.path',
    form_name: 'request_path',
    Component: JSONFilterPath,
    width: DROPDOWN_WIDTHS.large,
    icon: 'data_object',
    isActive: (path: string) => {
      return !!path;
    },
    formatForForm: (_, all_filters) => {
      return all_filters?.request?.path || '';
    },
    formatForQuery: (v: string) => {
      if (v) {
        return { path: v };
      }
      return undefined;
    },
    formatForDB: (v: string) => (v ? { request: { path: v } } : undefined),
    valuesToString: (v: string) => {
      if (!v) return undefined;
      return v.length > REQUEST_MAX_LENGTH ? v.substring(0, REQUEST_MAX_LENGTH) + '...' : v;
    },
  },
  webhook_id: {
    label: 'Connections',
    priority: 'primary',
    filter_key: 'webhook_id',
    form_name: 'webhook_id',
    center_label: true,
    Component: ResourcesFilter,
    width: DROPDOWN_WIDTHS.large,
    component_props: {
      api_root: 'webhooks',
      name: 'webhook_id',
    },
    icon: 'connections',
    ...common_formatters.multi_select('webhook_id'),
    valuesToString: (v: string[], data_by_id) =>
      truncateFilterValue(v.map((id) => `${data_by_id?.webhook_id[id]?.full_name}`)),
  },
  source_id: {
    label: 'Sources',
    priority: 'secondary',
    filter_key: 'source_id',
    form_name: 'source_id',
    Component: ResourcesFilter,
    component_props: {
      api_root: 'sources',
      title: 'Filter by source',
      placeholder: 'Find a source',
    },
    icon: 'source',
    width: DROPDOWN_WIDTHS.large,
    ...common_formatters.multi_select('source_id'),
    valuesToString: (v: string[], data_by_id) =>
      truncateFilterValue(v.map((id) => data_by_id?.source_id[id]?.name)),
  },
  destination_id: {
    label: 'Destinations',
    priority: 'secondary',
    filter_key: 'destination_id',
    form_name: 'destination_id',
    Component: ResourcesFilter,
    component_props: {
      api_root: 'destinations',
      title: 'Filter by destination',
      placeholder: 'Find a destination',
    },
    icon: 'destinations',
    width: DROPDOWN_WIDTHS.large,
    ...common_formatters.multi_select('destination_id'),
    valuesToString: (v: string[], data_by_id) =>
      truncateFilterValue(v.map((id) => data_by_id?.destination_id[id]?.name)),
  },
  delivery_group: {
    label: 'Delivery group',
    priority: 'secondary',
    filter_key: 'delivery_group',
    form_name: 'delivery_group',
    Component: null,
    icon: 'group',
    hidden: true,
    center_label: true,
    isActive: (v: (string | null)[]) => v?.length > 0,
    formatForForm: (_, all_filters) => {
      const delivery_group = all_filters.delivery_group as
        | string
        | (string | null)[]
        | null
        | undefined;

      if (delivery_group === undefined) {
        return [];
      }

      return Array.isArray(delivery_group) ? delivery_group : [delivery_group];
    },
    formatForQuery: (v: (string | null)[]) => {
      if (!v?.length) return undefined;
      return v.length === 1 && v[0] === null ? null : v;
    },
    formatForDB: () => undefined,
    valuesToString: (v: (string | null)[]) =>
      truncateFilterValue(v.map((delivery_group) => delivery_group ?? 'Unknown')),
  },
  event_status: {
    label: 'Status',
    priority: 'primary',
    filter_key: 'status',
    form_name: 'status',
    Component: MultiSelectFilter,
    component_props: {
      hide_label: true,
      hide_search: true,
      title: 'Filter by event status',
      placeholder: 'Find a event status',
      options: event_status_options,
    },
    icon: 'status',
    ...common_formatters.multi_select('status'),
    valuesToString: (v: string[]) =>
      truncateFilterValue(
        v.map(
          (val) =>
            event_status_options.find((o) => o.value === val)?.label || capitalizeFirstLetter(val),
        ),
      ),
  },
  connection_status: {
    label: 'Status',
    priority: 'primary',
    filter_key: 'status',
    form_name: 'status',
    Component: SelectFilter,
    component_props: {
      options: [
        {
          label: 'Enabled',
          value: 'active',
        },
        {
          label: 'Paused',
          value: 'paused',
        },
        {
          label: 'Disabled',
          value: 'disabled',
        },
      ],
    },
    icon: 'status',
    ...common_formatters.select('status'),
  },
  log_level: {
    label: 'Log Level',
    priority: 'primary',
    filter_key: 'log_level',
    form_name: 'log_level',
    Component: MultiSelectFilter,
    component_props: {
      hide_label: true,
      hide_search: true,
      options: [
        { value: 'debug', label: 'Debug' },
        { value: 'info', label: 'Info' },
        { value: 'warn', label: 'Warn' },
        { value: 'error', label: 'Error' },
        { value: 'fatal', label: 'Fatal' },
      ],
    },
    icon: 'status',
    ...common_formatters.multi_select('log_level'),
  },
  events_count: {
    label: 'Event Count',
    priority: 'secondary',
    hide_label: true,
    filter_key: 'events_count',
    form_name: 'events_count',
    Component: NumberRangeFilter,
    component_props: {
      title: 'Filter by event count',
      min_label: 'Min',
      max_label: 'Max',
    },
    icon: 'events',
    isActive: (v: { min: string; max: string }) => {
      if (!isEmpty(v.min) || !isEmpty(v.max)) {
        return true;
      }
      return false;
    },
    formatForForm: (v) => {
      return v || {};
    },
    formatForQuery: (v: { min?: number; max?: number }) => {
      return numberRangeFormatForQuery(v) as any;
    },
    formatForDB: (v: { min?: number; max?: number }) => {
      v = numberRangeFormatForDB(v);
      return { events_count: v };
    },
    valuesToString: (v: { min: number; max: number }) => {
      if (isEmpty(v.min) && isEmpty(v.max)) return;
      if (isEmpty(v.min)) {
        return `Maximum ${v.max}`;
      }
      return `${v.min ? v.min : ''} ${v.max ? 'to ' + v.max : ''}`;
    },
  },
  cli_events_count: {
    label: 'CLI Events',
    priority: 'secondary',
    filter_key: 'cli_events_count',
    form_name: 'cli_events_count',
    Component: NumberRangeFilter,
    icon: 'terminal',
    isActive: (v: { min: string; max: string }) => {
      if (!v) return false;
      if (!isEmpty(v.min) || !isEmpty(v.max)) {
        return true;
      }
      return false;
    },
    formatForForm: (v) => {
      return v || {};
    },
    formatForQuery: (v: { min?: number; max?: number }) => {
      return numberRangeFormatForQuery(v) as any;
    },
    formatForDB: (v: { min?: number; max?: number }) => {
      v = numberRangeFormatForDB(v);
      return { events_count: v };
    },
    valuesToString: (v: { min: number; max: number }) => {
      if (isEmpty(v.min) && isEmpty(v.max)) return;
      if (isEmpty(v.min)) {
        return `Maximum ${v.max}`;
      }
      return `${v.min ? v.min : ''} ${v.max ? 'to ' + v.max : ''}`;
    },
  },
  ignored_events: {
    label: 'Ignored Event Count',
    priority: 'secondary',
    hide_label: true,
    filter_key: 'ignored_count',
    form_name: 'ignored_count',
    Component: NumberRangeFilter,
    component_props: {
      title: 'Filter by ignored event count',
      min_label: 'Min',
      max_label: 'Max',
    },
    icon: 'block',
    isActive: (v: { min: string; max: string }) => {
      if (!isEmpty(v.min) || !isEmpty(v.max)) {
        return true;
      }
      return false;
    },
    formatForForm: (v: string | string[] | { [k: string]: any }) => {
      return v || {};
    },
    formatForQuery: (v?: { min?: number; max?: number } | any) => {
      return numberRangeFormatForQuery(v);
    },
    formatForDB: (v: { min?: number; max?: number }) => {
      v = numberRangeFormatForDB(v);
      return { ignored_count: v };
    },
    valuesToString: (v: { min: number; max: number }) => {
      if (isEmpty(v.min) && isEmpty(v.max)) return;
      if (isEmpty(v.min)) {
        return `Maximum ${v.max}`;
      }
      return `${v.min ? v.min : ''} ${v.max ? 'to ' + v.max : ''}`;
    },
  },
  request_status: {
    label: 'Status',
    priority: 'primary',
    filter_key: 'status',
    form_name: 'status',
    Component: SelectFilter,
    width: DROPDOWN_WIDTHS.small,
    component_props: {
      options: [
        { value: 'accepted', label: 'Accepted' },
        { value: 'rejected', label: 'Rejected' },
      ],
    },
    icon: 'status',
    ...common_formatters.select('status'),
  },
  rejection_cause: {
    label: 'Rejection Cause',
    priority: 'secondary',
    filter_key: 'rejection_cause',
    form_name: 'rejection_cause',
    Component: MultiSelectFilter,
    component_props: {
      hide_label: true,
      title: 'Filter by rejection cause',
      placeholder: 'Find a rejection cause',
      options: Object.entries(rejection_cause_labels).map(([k, v]) => ({
        value: k,
        label: v,
      })),
    },
    icon: 'error',
    ...common_formatters.multi_select('rejection_cause'),
  },
  response_status: {
    label: 'Response Status',
    priority: 'secondary',
    hide_label: true,
    filter_key: 'response.response_status',
    form_name: 'response_status',
    Component: NumberRangeFilter,
    component_props: {
      title: 'Filter by response status',
      min_label: 'Min',
      max_label: 'Max',
    },
    icon: 'http',
    validate: (v: { min?: number; max?: number }) => {
      const errors: { min?: string; max?: string } = {};
      const has_min = !isEmpty(v?.min);
      const has_max = !isEmpty(v?.max);
      if (has_min && (v.min! < 200 || v.min! > 599)) {
        errors.min = 'Minimum response status must be between 200 and 599';
        return errors;
      }
      if (has_max && (v.max! < 200 || v.max! > 599)) {
        errors.max = 'Maximum response status must be between 200 and 599';
        return errors;
      }
      if (has_min && has_max && v.min! > v.max!) {
        errors.min = 'Minimum response status must be lower than maximum';
      }
      return errors;
    },
    isActive: (v: { min: string; max: string }) => {
      if (!isEmpty(v?.min) || !isEmpty(v?.max)) {
        return true;
      }
      return false;
    },
    formatForForm: (_, all_filters) => {
      return all_filters.response_status || {};
    },
    formatForQuery: (v: { min?: number; max?: number }) => {
      const formatted = numberRangeFormatForQuery(v);
      if (!formatted) return undefined;
      return { response_status: formatted };
    },
    formatForDB: (v: { min?: number; max?: number }) => {
      const formatted = numberRangeFormatForDB(v);
      if (!formatted || Object.keys(formatted).length === 0) return undefined;
      return { response: { response_status: formatted } };
    },
    valuesToString: (v: { min: number; max: number }) => {
      if (isEmpty(v?.min) && isEmpty(v?.max)) return;
      const min = v.min || 200;
      const max = v.max;
      if (isEmpty(v.min) && !isEmpty(v.max)) {
        return `Maximum ${v.max}`;
      }
      return `${min} ${max ? 'to ' + max : ''}`;
    },
  },
  error_code: {
    label: 'Error Codes',
    priority: 'secondary',
    filter_key: 'response.error_code',
    form_name: 'error_code',
    Component: MultiSelectFilter,
    component_props: {
      hide_label: true,
      title: 'Filter by error code',
      placeholder: 'Find an error code',
      options: Object.keys(getErrorCodeLabels()).map((k) => ({ label: k, value: k })),
    },
    icon: 'error',
    center_label: true,
    isActive: (v: string[]) => {
      if (v?.length > 0) {
        return true;
      }
      return false;
    },
    formatForForm: (_, all_filters) => all_filters.error_code || [],
    formatForQuery: (v: string[]) => (v?.length > 0 ? { error_code: v } : undefined),
    formatForDB: (v: string[]) => (v?.length > 0 ? { response: { error_code: v } } : undefined),
    valuesToString: (v: string[]) => truncateFilterValue(v.map(capitalizeFirstLetter)),
  },
  attempts: {
    label: 'Attempts',
    priority: 'secondary',
    hide_label: true,
    filter_key: 'attempts',
    form_name: 'attempts',
    Component: NumberRangeFilter,
    component_props: {
      title: 'Filter by attempt count',
      min_label: 'Min',
      max_label: 'Max',
    },
    icon: 'attempt',
    isActive: (v: { min: string; max: string }) => {
      if (!isEmpty(v.min) || !isEmpty(v.max)) {
        return true;
      }
      return false;
    },
    formatForForm: (v) => {
      return v || {};
    },
    formatForQuery: (v?: { min?: number; max?: number } | any) => {
      return numberRangeFormatForQuery(v);
    },
    formatForDB: (v: { min?: number; max?: number }) => {
      v = numberRangeFormatForDB(v);
      return { attempts: v };
    },
    valuesToString: (v: { min: number; max: number }) => {
      if (isEmpty(v.min) && isEmpty(v.max)) return;
      if (isEmpty(v.min)) {
        return `Maximum ${v.max}`;
      }
      return `${v.min ? v.min : ''} ${v.max ? 'to ' + v.max : ''}`;
    },
  },

  cli_user_id: {
    label: 'CLI Users',
    priority: 'secondary',
    filter_key: 'cli_user_id',
    form_name: 'cli_user_id',
    Component: CliUserFilter,
    icon: 'person',
    center_label: true,
    isActive: (v: string[]) => v?.length > 0,
    formatForForm: (v: string[]) => (v?.length ? v : []),
    formatForQuery: (v: string[]) => (v?.length ? v : undefined),
    formatForDB: (v: string[]) => (v?.length ? { cli_user_id: v } : undefined),
    valuesToString: (v: string[], data_by_id, user: User) =>
      truncateFilterValue(
        v.map((id) =>
          data_by_id?.team_members[id]?.user_id === user.id
            ? 'Myself'
            : data_by_id?.team_members[id]?.user_name,
        ),
      ),
  },
  search: {
    label: 'Search',
    priority: 'secondary',
    filter_key: 'search_term',
    form_name: 'search_term',
    validate: (v) => {
      if (v && v.length < 3) {
        return { search_term: 'Search term must be at least 3 characters' };
      }
      return {};
    },
    Component: ({ name, delete_button, onSubmit, onDelete }) => {
      const [{ value }, { error }, { setValue }] = useField(name);
      return (
        <>
          <Div flex={{ align: 'center' }}>
            <Search
              w={50}
              onChange={(v) => {
                if (!v) {
                  onDelete();
                } else {
                  setValue(v);
                }
              }}
              onBlur={onSubmit}
              value={value}
              placeholder={'Search for a value found in the headers, body, query or path...'}
            />
            {delete_button && delete_button}
          </Div>
          {error?.[name] && (
            <Text danger size="s" m={{ t: 2, b: 2 }}>
              {error[name]}
            </Text>
          )}
        </>
      );
    },
    icon: 'search',
    hidden: true,
    ...common_formatters.single_value('search_term'),
    isActive: (v: string[]) => {
      if (v?.length >= 3) {
        return true;
      }
      return false;
    },
    formatForQuery: (v) => v || undefined,
  },
  issue_id: {
    label: 'Issue',
    priority: 'secondary',
    filter_key: 'issue_id',
    form_name: 'issue_id',
    Component: null,
    icon: 'inbox',
    hidden: true,
    ...common_formatters.single_value('issue_id'),
  },
  bulk_retry_id: {
    label: 'Bulk operation',
    priority: 'secondary',
    filter_key: 'bulk_retry_id',
    form_name: 'bulk_retry_id',
    Component: null,
    icon: 'retry',
    hidden: true,
    ...common_formatters.single_value('bulk_retry_id'),
  },
  scheduled_attempts: {
    label: 'Scheduled Attempts',
    priority: 'secondary',
    filter_key: 'scheduled_attempts',
    form_name: 'scheduled_attempts',
    Component: SelectFilter,
    width: DROPDOWN_WIDTHS.medium,
    component_props: {
      block: true,
      title: 'Filter by scheduled attempts',
      options: [
        { value: 'true', label: 'Has scheduled attempts' },
        { value: 'false', label: 'No scheduled attempts' },
      ],
    },
    icon: 'avg_pace',
    ...common_formatters.select('scheduled_attempts'),
    valuesToString: (v: string) => (v === 'true' ? 'Yes' : 'No'),
  },
};

export const search_filter_component: FilterComponent = filters_components.search;
export const date_filter_component: FilterComponent = filters_components.date;

export const event_list_filters: FilterComponent[] = [
  filters_components.event_status,
  filters_components.webhook_id,
  filters_components.request_body,
  filters_components.request_headers,
  filters_components.request_query,
  filters_components.request_path,
  filters_components.source_id,
  filters_components.destination_id,
  filters_components.delivery_group,
  filters_components.response_status,
  filters_components.error_code,
  filters_components.attempts,
  filters_components.scheduled_attempts,
  filters_components.cli_user_id,
  filters_components.bulk_retry_id,
  filters_components.issue_id,
];

export const request_list_filters: FilterComponent[] = [
  filters_components.request_status,
  { ...filters_components.source_id, priority: 'primary' },
  filters_components.request_body,
  filters_components.request_headers,
  filters_components.request_query,
  filters_components.request_path,
  filters_components.rejection_cause,
  filters_components.bulk_retry_id,
  filters_components.events_count,
  filters_components.ignored_events,
];

export const transformation_execution_list_filters: FilterComponent[] = [
  filters_components.webhook_id,
  filters_components.log_level,
  filters_components.date_visible,
];

export const transformation_issue_fatal_execution_list_filters: FilterComponent[] = [
  filters_components.webhook_id,
  filters_components.date_visible,
];

// Outpost-specific filter components

const outpost_filters_components: Record<string, FilterComponent> = {
  status: {
    label: 'Status',
    priority: 'primary',
    filter_key: 'status',
    form_name: 'status',
    Component: SelectFilter,
    component_props: {
      title: 'Filter by status',
      options: [
        { value: 'success', label: 'Success' },
        { value: 'failed', label: 'Failed' },
      ],
    },
    icon: 'success',
    width: DROPDOWN_WIDTHS.medium,
    ...common_formatters.select('status'),
  },
  tenant_id: {
    label: 'Tenants',
    priority: 'primary',
    filter_key: 'tenant_id',
    form_name: 'tenant_id',
    Component: OutpostTenantsFilter,
    component_props: {
      title: 'Filter by tenant',
      placeholder: 'Find a tenant',
    },
    icon: 'group',
    width: DROPDOWN_WIDTHS.large,
    ...common_formatters.multi_select('tenant_id'),
    valuesToString: (v: string[], data_by_id) =>
      truncateFilterValue(v.map((id) => getOutpostTenantLabel(id, data_by_id?.tenant_id?.[id]))),
  },
  topic: {
    label: 'Topics',
    priority: 'primary',
    filter_key: 'topic',
    form_name: 'topic',
    Component: OutpostTopicsFilter,
    component_props: {
      title: 'Filter by topic',
      placeholder: 'Find a topic',
    },
    icon: 'email',
    width: DROPDOWN_WIDTHS.large,
    ...common_formatters.multi_select('topic'),
    valuesToString: (v: string[]) => truncateFilterValue(v),
  },
  event_id: {
    label: 'Event',
    priority: 'secondary',
    filter_key: 'event_id',
    form_name: 'event_id',
    Component: TextFilter,
    component_props: {
      title: 'Filter by event ID',
      placeholder: 'Find an event ID',
    },
    icon: 'events',
    width: DROPDOWN_WIDTHS.large,
    ...common_formatters.single_value('event_id'),
  },
  destination_id: {
    label: 'Destination',
    priority: 'secondary',
    filter_key: 'destination_id',
    form_name: 'outpost_destination_id',
    Component: TextFilter,
    component_props: {
      title: 'Filter by destination ID',
      placeholder: 'Find a destination ID',
    },
    icon: 'destination',
    width: DROPDOWN_WIDTHS.large,
    isActive: (v: string) => !!v?.length,
    formatForForm: (filters) => (filters as string) || '',
    formatForQuery: (v: string) => v || undefined,
    formatForDB: (v: string) => (v ? { destination_id: v } : undefined),
    valuesToString: (v: string) => v,
  },
};

export const outpost_date_filter_component: FilterComponent = {
  ...hidden_date_filter,
  formatForDB: (v: { relative: string; min: string; max: string }) => {
    if (v?.relative) {
      return { time: { relative: v.relative } };
    }
    if (v?.min || v?.max) {
      return { time: { min: v.min, max: v.max } };
    }
    return undefined;
  },
};

export const outpost_event_list_filters: FilterComponent[] = [
  outpost_filters_components.tenant_id,
  outpost_filters_components.topic,
];

export const outpost_attempt_list_filters: FilterComponent[] = [
  outpost_filters_components.status,
  outpost_filters_components.tenant_id,
  outpost_filters_components.topic,
  outpost_filters_components.event_id,
  outpost_filters_components.destination_id,
];
